/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React, { type ReactNode } from 'react';
import { useHistory } from 'react-router';

import { ListeTable, ListeTableContainer } from '@mairistem/liste';

import { baseUrlApp } from '@/routes';
import ActeDecesListeTableRow from './ActeDecesListeTableRow';

interface ActeDecesListeTableProps {
  items?: Array<any>;
  count?: number;
  perPage?: number;
  loading?: boolean;
  onLoad: any;
  onSort: any;
  /** Réduit la liste à la seule colonne du nom, pour la sidebar de la fiche. */
  sidebar?: boolean;
}

/**
 * Les huit colonnes de la liste des décès : exactement la forme de la liste des
 * naissances, à la date près — « Décédé le » y remplace « Né le ».
 *
 * ⚠️ Le conjoint ou ex-conjoint n'y figure pas, alors que la projection le
 * remonte (`nom_ex_conjoint`) : la colonne reste disponible côté API si la liste
 * doit la reprendre.
 *
 * ⚠️ Les `width` doivent totaliser 16, comme sur les trois autres listes.
 */
const ActeDecesListeTable = (props: ActeDecesListeTableProps) => {
  const { items, count, perPage, loading, onLoad, onSort, sidebar } = props;

  const history = useHistory();

  const renderRow = (item: any) => (
    <ActeDecesListeTableRow item={item} sidebar={sidebar} />
  );

  const handleSelect = (selectedItem: any) => {
    const identifiant = selectedItem?.identifiant ?? selectedItem?.id;
    if (identifiant != null) {
      history.push(`${baseUrlApp}deces/${identifiant}`);
    }
  };

  const listItems = React.useMemo(() => {
    const list = items || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (list as any[]).map((item) => {
      if (item?.identifiant == null && item?.id != null) {
        return { ...item, identifiant: item.id };
      }
      return item;
    });
  }, [items]);

  return (
    <ListeTableContainer
      // @ts-ignore
      items={listItems}
    >
      <ListeTable
        items={listItems}
        loading={loading}
        perPage={perPage}
        count={count}
        onLoad={onLoad}
        onSort={onSort}
        onSelect={handleSelect}
      >
        {/* Colonnes en enfants DIRECTS du Header : cf. MentionListeTable. */}
        {sidebar ? (
          <ListeTable.Header>
            <ListeTable.Column name="nom" width={16}>
              Défunt
            </ListeTable.Column>
          </ListeTable.Header>
        ) : (
          <ListeTable.Header>
            <ListeTable.Column name="registre" width={3}>
              Registre
            </ListeTable.Column>
            <ListeTable.Column name="typeActe" width={2}>
              Type d&apos;acte
            </ListeTable.Column>
            <ListeTable.Column name="numero" sortable width={2}>
              N° acte et ordre
            </ListeTable.Column>
            <ListeTable.Column name="dresse" sortable width={2}>
              Dressé
            </ListeTable.Column>
            <ListeTable.Column name="dateDeces" sortable width={1}>
              Décédé le
            </ListeTable.Column>
            <ListeTable.Column name="defunt" width={2}>
              Défunt
            </ListeTable.Column>
            {/*
                « Parent 1 / 2 » et non « Père / Mère », comme la liste des
                naissances : le décès n'impose pas de rôle à ses parents
                (cf. `constants/rolesParentsParTypeActe`, où il n'a pas
                d'entrée). Seule la reconnaissance nomme les rôles.
              */}
            <ListeTable.Column name="parent1" width={2}>
              Parent 1
            </ListeTable.Column>
            <ListeTable.Column name="parent2" width={2}>
              Parent 2
            </ListeTable.Column>
          </ListeTable.Header>
        )}
        {renderRow as unknown as ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default ActeDecesListeTable;
